import { useState, useCallback, useRef } from 'react';
import { ApiService } from '../services/api';
import { ManhwaRecap, FilterOptions } from '../types';
import { profiler } from '../utils/profiler';

export const useRecaps = () => {
  const [recaps, setRecaps] = useState<ManhwaRecap[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const requestCounterRef = useRef(0);
  const currentFiltersRef = useRef<FilterOptions>();

  const fetchRecaps = useCallback(async (filters: FilterOptions, pageNum: number = 1) => {
    const isLoadMore = pageNum > 1;
    const currentRequest = ++requestCounterRef.current;
    currentFiltersRef.current = filters;

    const profilerName = `fetchRecaps-${isLoadMore ? 'loadMore' : 'initial'}`;
    profiler.start(profilerName, {
      page: pageNum,
      filters: {
        startsWith: filters.startsWith,
        searchQuery: filters.searchQuery,
        series_name: filters.series_name,
        language: filters.language,
        channelId: filters.channelId,
      }
    });

    setLoading(true);
    setError(null);

    try {
      const apiParams: any = {
        page: pageNum,
        limit: 20,
        search: filters.searchQuery,
        series_name: filters.series_name,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        channelId: filters.channelId,
        language: filters.language,
        min_duration: filters.minDuration,
        max_duration: filters.maxDuration,
        startsWith: filters.startsWith,
      };

      profiler.start('api.getRecaps', { params: apiParams });
      const response = await ApiService.getRecaps(apiParams);
      profiler.end('api.getRecaps', {
        resultCount: response.data.length,
        total: response.total,
        hasMore: response.hasMore
      });

      if (requestCounterRef.current === currentRequest) {
        setRecaps(prev => isLoadMore ? [...prev, ...response.data] : response.data);
        setHasMore(response.hasMore);
        setTotal(response.total);
        setPage(pageNum);
        profiler.end(profilerName, { 
          success: true,
          recapsCount: response.data.length,
          total: response.total
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch recaps';
      profiler.end(profilerName, { success: false, error: errorMessage });
      if (requestCounterRef.current === currentRequest) {
        setError(errorMessage);
      }
    } finally {
      if (requestCounterRef.current === currentRequest) {
        setLoading(false);
      }
    }
  }, []);

  // Create a ref to hold the state that loadMore depends on.
  // This makes the loadMore function itself stable.
  const loadMoreStateRef = useRef({ loading, hasMore, page });
  loadMoreStateRef.current = { loading, hasMore, page };

  const loadMore = useCallback(() => {
    const { loading, hasMore, page } = loadMoreStateRef.current;
    if (loading || !hasMore || !currentFiltersRef.current) return;
    fetchRecaps(currentFiltersRef.current, page + 1);
  }, [fetchRecaps]);

  return {
    recaps,
    loading,
    error,
    hasMore,
    total,
    fetchRecaps,
    loadMore,
    setRecaps, // Expose setRecaps to allow clearing the list
  };
};